import dayjs from 'dayjs'
import { PriceHistory } from 'store/queries/bolt-api/contracts/types.ts'
import { GRANULARITY_DURATION } from 'constants/datetime.ts'
import { Granularity } from 'types/contracts.ts'
import { addBelgianTime, formatHour } from 'utils/date.ts'
import { OffPeakRange } from './types.ts'

/**
 * Get the index of the entry whose period contains the current time, or -1 if there is none (e.g. in the tomorrow view)
 * @param {PriceHistory[]} data
 * @param {Granularity} granularity
 * @returns {number}
 */
export const getIndexOfCurrentPrice = (data: PriceHistory[], granularity: Granularity): number => {
  const { amount, unit } = GRANULARITY_DURATION[granularity]
  const now = dayjs()

  return data.findIndex(({ date }) => !now.isBefore(date) && now.isBefore(addBelgianTime(date, amount, unit)))
}

/**
 * Groups the periods with the lowest prices into consecutive timeframes, e.g. [{ from: '09:00', until: '10:30' }]. The
 * end of a timeframe is the end of its last period, which is the start of the period after it
 *
 * @param {PriceHistory[]} data
 * @param {number[]} lowestPriceIndexes
 * @param {Granularity} granularity
 * @returns {OffPeakRange[]}
 */
export const getOffPeakRanges = (data: PriceHistory[], lowestPriceIndexes: number[], granularity: Granularity): OffPeakRange[] => {
  const { amount, unit } = GRANULARITY_DURATION[granularity]
  const getPeriodEnd = (index: number): string => formatHour(addBelgianTime(data[index].date, amount, unit))
  const sortedIndexes = [...lowestPriceIndexes].sort((a, b) => a - b)

  return sortedIndexes.reduce((ranges: OffPeakRange[], current, idx) => {
    if (!data[current]?.date) return ranges

    // Extend the last timeframe if the current period directly follows it, start a new one otherwise
    if (ranges.length && current === sortedIndexes[idx - 1] + 1) {
      ranges[ranges.length - 1].until = getPeriodEnd(current)
    } else {
      ranges.push({ from: formatHour(data[current].date), until: getPeriodEnd(current) })
    }

    return ranges
  }, [])
}

/**
 * Get the indexes of the lowest prices in the data array
 * @param {PriceHistory[]} data
 * @param {number} amountOfIndexes
 * @param {'high' | 'low'} identifier
 * @returns {number[]}
 */
export const getIndexesOfLowestOrHighestPrices = (data: PriceHistory[], amountOfIndexes: number, identifier: 'high' | 'low'): number[] => {
  if (!data.length) return []

  // create a copy of the original array to avoid modifying it
  const sortedData = [...data]

  // Sort the array based on price in ascending order
  sortedData.sort((a, b) => (identifier === 'low' ? a.price - b.price : b.price - a.price))

  // Get the indexes of the first four elements (lowest prices)
  const indexes: number[] = []
  const limit = Math.min(amountOfIndexes, sortedData.length)
  for (let i = 0; i < limit; i++) {
    const index = data.findIndex((item) => item === sortedData[i])
    indexes.push(index)
  }

  return indexes
}
